import React, { FormEvent, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import AmpButton from "../../common/AmpButton/AmpButton";
import SmallHeading from "../../common/AmpSmallHeading/AppSmallHeading";
import AmpInput from "../../common/AmpInput/AmpInput";
import AmpToolTip from "../../common/AmpToolTip/AmpToolTip";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../redux/actions/auth/auth";
import { RootState } from "../../../redux/reducers/root";
import AmpFormError from "../../common/AmpFormError/AmpFormError";
import { LoginState } from "../../../redux/reducers/getAuthReducer/authLoginReducer";
import { setToken } from "../../../redux/actions/auth/token";
import AmpCaptcha from "../../common/AmpCaptcha/AmpCaptcha";
import AmpInlineLoadingSpinner from "../../common/AmpInlineLoadingSpinner/AmpInlineLoadingSpinner";
import ReactGA from "react-ga4";

export default function NewUserForm(): JSX.Element {

    const { t } = useTranslation('login');

    const loginResult = useSelector((state: RootState) => state.authReducer.login as LoginState)

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [emailId, setEmailId] = useState("");
    const [captchaKey, setCaptchaKey] = useState(1);

    const usernameId = "login-useremail";
    const passwordId = "login-password";

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        const data: FormData = new FormData(e.target as HTMLFormElement);
        const values = Object.fromEntries(data.entries()) as Record<string, unknown>;
        setCaptchaKey(captchaKey + 1);
        dispatch(login(values));        
    }

    useEffect(() => {
        // Reset path has a token but is a reset token therefore can't follow normal path
        if (loginResult.reset_req && emailId !== "") {
            // Redirect to change password page
            navigate(`/auth/reset2/${loginResult.user_profile_id}/${loginResult.token}`);
        } else {
            
            const storageToken = window.localStorage.getItem("amptoken");
            if (loginResult.token && storageToken === null) {
                window.localStorage.setItem("amptoken", loginResult.token);
                dispatch(setToken(true));
            }
            const stoargeRefresh = localStorage.getItem("amprefresh");
            if (loginResult.refresh && stoargeRefresh === null) {
                window.localStorage.setItem("amprefresh", loginResult.refresh);
            }
            if(loginResult.tip2){
                window.localStorage.setItem("ampSplash", "true");
            }
            const userProfileId = window.localStorage.getItem("userProfileId");
            if (loginResult.user_profile_id && userProfileId === null) {
                window.localStorage.setItem("userProfileId", loginResult.user_profile_id);
                window.localStorage.setItem("surveyId", loginResult.user_profile_id)
                // Send a custom event
                ReactGA.event({
                    category: "Login/Registration",
                    action: "Logged In",
                    label: `${loginResult.user_profile_id} has logged in`, // optional
                });
            }

            const email = window.localStorage.getItem("email");
            if (loginResult.user_profile_id && email === null) {
                window.localStorage.setItem("email", emailId);
            }


            const sessionExp = window.localStorage.getItem("sessionExp");
            if (loginResult.exp && sessionExp === null) {
                window.localStorage.setItem("sessionExp", loginResult.exp.toString());
            }

            if (loginResult.show_survey) {
                window.localStorage.setItem("showSurvey", "Y");
            } else {
                window.localStorage.setItem("showSurvey", "N");
            }

            loginResult.is_agent ?
                window.localStorage.setItem("isAgent", "Y"):
                window.localStorage.setItem("isAgent", "N")

            if (!loginResult.verified && emailId !== "" && loginResult.user_profile_id) {
                // Redirect to email verify page
                navigate(`/auth/verify/${loginResult.user_profile_id}/${emailId}`)
            }
        }
    }, [loginResult, emailId, dispatch, navigate])

    const loginError = !loginResult.success && emailId !== "" && loginResult.status === 403 && !loginResult.loading;
    const isCaptchaFailed = loginResult.captcha === false && loginResult.captcha !== null;
    const suspendedError = loginResult.suspended === true;
    const loginErrorMessage = isCaptchaFailed ? t("verify-captcha") : loginError ? t('login-error') : suspendedError ? t("suspended") : "";

    const [resetting, setReset] = useState(false);

    useEffect(() => {
        if (resetting) {
            setTimeout(() => {
                setReset(false);
            }, 400);
        }
    }, [resetting])

    const reset = () => {
        setReset(true)
    }

    return (
        <>
            {resetting &&
                <div className="refreshing refreshing-small">
                    <AmpInlineLoadingSpinner />
                </div>
            }
            {!resetting && (
                <form onSubmit={(e) => { onSubmit(e) }}>
                    <div className="login-container-mid">
                        <SmallHeading for={usernameId}>
                            {t('reg-user-uid') as string}
                            <AmpToolTip id={`tooltip-id-userid`} content={t("reg-user-uid-tooltip")} />
                        </SmallHeading>
                        <AmpInput noTimer={true} onInput={(email) => setEmailId(email)} type="text" id={usernameId} name="username" />
                        <div className="flex-both-sides">
                            <SmallHeading for={passwordId}>
                                {t('reg-user-pwd') as string}
                                <AmpToolTip id={`tooltip-id-userid-password`} content={t("reg-user-tooltip")} />
                            </SmallHeading>
                            <Link className="amp-anchor forgot" to="/auth/reset">{t("forgot") as string}</Link>
                        </div>

                        <AmpInput type="password" id={passwordId} name={"password"} />

                        <AmpCaptcha key={captchaKey} />
                        <AmpFormError error={(loginError || isCaptchaFailed || suspendedError) && (emailId !== "")} errorMessage={loginErrorMessage} />
                    </div>
                    <div className="login-container-footer">
                        <Link rel="noreferrer noopener" target="_blank" className="amp-anchor" to="/auth/contact-us">{t("need-help") as string}</Link>
                        <div>
                            <AmpButton buttonType="reset" type="cancel" onClick={() => reset()} text={t("cancel")} />
                            <AmpButton buttonType="submit" type="submit" onClick={() => null} text={t("sign-in")} />
                        </div>
                    </div>
                </form>
            )}
        </>
    )
}